import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { SelectedBenchmark } from '../../../shared/constants/filter/filter';
import { useFilterData } from '../../../utilities/hooks/useFilterData';
import useTracking from '../../../utilities/hooks/useTracking';
import useFilters from '../../../utilities/hooks/useFilters';
import { EventActions, EventNames } from '../../../shared/constants/analytics';
import RadioGroup from '../../widgets/RadioGroup';
import { useDeviceType } from '../../../utilities/common/deviceType';
import { twMerge } from 'tailwind-merge';
import Select, { SelectOption } from '../../widgets/Select';
import { IndustryStandardType } from '../IndustryStandards/constants';

function BenchmarkFilter({
  selectedRadio,
  setSelectedRadio,
}: {
  selectedRadio: SelectedBenchmark;
  setSelectedRadio: (value: SelectedBenchmark) => void;
}) {
  const isMobile = useDeviceType();
  const { setBenchmarkType, benchmarkType } = useFilters();

  const { configData } = useFilterData();
  const { trackEvent } = useTracking();
  const currentPath = useLocation().pathname;
  const { t } = useTranslation();

  const pageName = currentPath.replace(/^\/d\//, '').replace(/-/g, '_');

  const industryOptions: SelectOption<number>[] = (configData?.industries ?? [])
    .slice(1)
    .filter((option) => option.meta.type === IndustryStandardType.SECTOR)
    .map((option) => ({
      label: option.name,
      value: option.id,
      key: option.id,
    }));

  const handleRadioChange = (value: SelectedBenchmark) => {
    setSelectedRadio(value);

    if (value === SelectedBenchmark.GLOBAL) {
      setBenchmarkType(SelectedBenchmark.GLOBAL);
      trackEvent(EventNames.WellBeingDashboard, {
        eventLabel: configData?.industries[0].name!,
        eventAction: EventActions.click,
        eventCategory: 'dimension_benchmark',
        eventSource: pageName,
      });
    }
  };

  const handleIndustryChange = (
    value: number,
    option: SelectOption<number>,
  ) => {
    setBenchmarkType(value);
    setSelectedRadio(SelectedBenchmark.INDUSTRY);

    trackEvent(EventNames.WellBeingDashboard, {
      eventLabel: option.label,
      eventAction: EventActions.click,
      eventCategory: 'dimension_benchmark',
      eventSource: pageName,
    });
  };

  return (
    <div className="mb-6 flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center">
      <span className="font-semibold">{t('BENCHMARK_AGAINST')}: </span>
      <RadioGroup<SelectedBenchmark>
        value={selectedRadio}
        onChange={handleRadioChange}
        name="benchmark"
        direction={isMobile ? 'column' : 'row'}
        options={[
          {
            label: `${t('GLOBAL')} ${t('AVERAGE')}`,
            value: SelectedBenchmark.GLOBAL,
          },
          {
            label: `${t('INDUSTRY')} ${t('AVERAGE')}`,
            value: SelectedBenchmark.INDUSTRY,
          },
        ]}
      />

      {/* Show Dropdown only if 'industry' selected */}
      {industryOptions.length > 0 && (
        <div
          className={twMerge(
            'relative hidden w-80 transition-opacity lg:inline-block lg:opacity-0',
            selectedRadio === SelectedBenchmark.INDUSTRY &&
              'block lg:opacity-100',
          )}
        >
          <Select<number>
            value={
              selectedRadio === SelectedBenchmark.INDUSTRY
                ? benchmarkType
                : null
            }
            onChange={handleIndustryChange}
            options={industryOptions}
            placeholder={t('SELECT')}
          />
        </div>
      )}
    </div>
  );
}

export default BenchmarkFilter;
